import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { reportApi } from '@/api';
import { Button, Icon, Modal, Textarea } from '@/components/ui';
import { errorMessage } from '@/hooks/useApiError';
import { useAuth } from '@/hooks/useAuth';
import { cn } from '@/utils/cn';

/** What is being reported, in the words the dialog uses. */
const NOUNS = {
  INTERNSHIP: 'internship',
  COMPANY: 'company',
  STUDENT: 'candidate',
};

/**
 * The reasons offered for each kind of target. A category gives the moderator
 * the shape of the problem at a glance; the details say what actually happened.
 */
const CATEGORIES = {
  INTERNSHIP: [
    { key: 'fraud', label: 'Scam or fraud', hint: 'Fake role, phishing, or identity theft' },
    { key: 'payment', label: 'Asks for payment', hint: 'A fee, deposit or purchase to apply' },
    {
      key: 'misleading',
      label: 'Misleading details',
      hint: 'Pay, role or location not as described',
    },
    { key: 'discrimination', label: 'Discriminatory', hint: 'Excludes people unfairly' },
    { key: 'offensive', label: 'Inappropriate content', hint: 'Offensive or explicit wording' },
    { key: 'other', label: 'Something else', hint: 'Tell us in the details below' },
  ],
  COMPANY: [
    {
      key: 'fraud',
      label: 'Fake or fraudulent company',
      hint: 'Not a real business, or impersonating one',
    },
    { key: 'payment', label: 'Asked me to pay', hint: 'Fees for training, kits or onboarding' },
    { key: 'conduct', label: 'Unprofessional conduct', hint: 'Harassment or abusive behaviour' },
    { key: 'misleading', label: 'Misleading information', hint: 'Profile details are untrue' },
    { key: 'other', label: 'Something else', hint: 'Tell us in the details below' },
  ],
  STUDENT: [
    { key: 'fake', label: 'Fake profile', hint: 'Not a real person, or impersonating someone' },
    {
      key: 'misleading',
      label: 'False information',
      hint: 'Education, experience or CV is untrue',
    },
    { key: 'conduct', label: 'Inappropriate behaviour', hint: 'Abusive or harassing messages' },
    { key: 'spam', label: 'Spam', hint: 'Mass or irrelevant applications' },
    { key: 'other', label: 'Something else', hint: 'Tell us in the details below' },
  ],
};

const MIN_DETAILS = 10;
// The server keeps 1000 characters; the category label goes in front.
const MAX_DETAILS = 900;

/**
 * "Report" — the only way something reaches a moderator without one going
 * looking for it.
 *
 * Deliberately quiet: a prominent report button invites use as a downvote,
 * and the queue behind it is read by a person.
 *
 * Filing changes nothing about the content, and the dialog says so, because a
 * reporter who expects it to vanish and watches it stay will report it again.
 * The reported party never sees a report or who made it (only administrators
 * can list them).
 *
 * `targetType` is 'INTERNSHIP', 'COMPANY' or 'STUDENT'; `targetName` names it
 * in the dialog.
 */
export function ReportButton({ targetType, targetId, targetName, label, className }) {
  const { isAuthenticated } = useAuth();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState(null);
  const [details, setDetails] = useState('');
  const [sent, setSent] = useState(false);

  const noun = NOUNS[targetType] ?? 'content';
  const categories = CATEGORIES[targetType] ?? CATEGORIES.INTERNSHIP;
  const chosen = categories.find((item) => item.key === category);
  const trimmed = details.trim();
  const ready = Boolean(chosen) && trimmed.length >= MIN_DETAILS && trimmed.length <= MAX_DETAILS;

  // Whether this user already reported it, so the button reads Reported and
  // stays that way after a reload. Only ever the user's own report.
  const statusKey = ['my-report', targetType, targetId];
  const mine = useQuery({
    queryKey: statusKey,
    queryFn: () => reportApi.getMyReport({ targetType, targetId }),
    enabled: isAuthenticated && Boolean(targetId),
    staleTime: 60_000,
  });
  const alreadyReported = mine.data?.reported === true;

  const report = useMutation({
    mutationFn: () =>
      reportApi.reportContent({
        targetType,
        targetId,
        reason: `${chosen.label}: ${trimmed}`,
      }),
    // The same answer whether this is the first report or the fifth: the
    // server answers identically on purpose.
    onSuccess: () => {
      setSent(true);
      queryClient.setQueryData(statusKey, {
        reported: true,
        reported_at: new Date().toISOString(),
      });
    },
  });

  // Reporting needs an account, so the button would only ever lead to a 401.
  if (!isAuthenticated) return null;

  function close() {
    setOpen(false);
    // Reset after the dialog has gone, so it does not visibly clear first.
    window.setTimeout(() => {
      setCategory(null);
      setDetails('');
      setSent(false);
      report.reset();
    }, 200);
  }

  if (alreadyReported && !open) {
    return (
      <span
        className={cn(
          'text-ink-muted inline-flex cursor-default items-center gap-1.5 px-2 py-1 text-xs font-medium',
          className,
        )}
        title="Our moderation team is reviewing your report."
        aria-disabled="true"
      >
        <Icon name="check" size={14} className="text-success-600" />
        Reported · under review
      </span>
    );
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={cn(
          'text-ink-muted hover:text-danger-700 hover:bg-danger-50 rounded-input inline-flex items-center gap-1.5 px-2 py-1 text-xs font-medium transition',
          className,
        )}
      >
        <Icon name="flag" size={14} />
        {label ?? `Report this ${noun}`}
      </button>

      <Modal
        open={open}
        title={sent ? 'Report received' : `Report this ${noun}`}
        description={
          sent ? undefined : targetName ? `You are reporting “${targetName}”.` : undefined
        }
        onClose={close}
        footer={
          sent ? (
            <Button onClick={close}>Done</Button>
          ) : (
            <>
              <Button variant="secondary" onClick={close} disabled={report.isPending}>
                Cancel
              </Button>
              <Button
                variant="danger"
                disabled={!ready}
                isLoading={report.isPending}
                onClick={() => report.mutate()}
              >
                Submit report
              </Button>
            </>
          )
        }
      >
        {sent ? (
          <div className="flex flex-col items-center gap-3 py-4 text-center">
            <span className="bg-success-50 text-success-600 grid size-12 place-items-center rounded-full">
              <Icon name="check" size={22} />
            </span>
            <p className="text-ink font-medium">Thank you for letting us know.</p>
            <p className="text-ink-muted max-w-sm text-sm">
              Our moderation team will review your report. The {noun} stays visible while we look
              into it, and your identity is never shared with them.
            </p>
          </div>
        ) : (
          <div className="space-y-5">
            <fieldset>
              <legend className="text-ink mb-2 text-[12px] font-medium">
                What is the problem? <span className="text-danger-600">*</span>
              </legend>
              <div className="grid gap-2 sm:grid-cols-2">
                {categories.map((item) => {
                  const active = item.key === category;
                  return (
                    <label
                      key={item.key}
                      className={cn(
                        'rounded-input flex cursor-pointer items-start gap-2.5 border px-3 py-2.5 transition',
                        active
                          ? 'border-brand-500 bg-brand-50/70 ring-brand-100 ring-2'
                          : 'border-line hover:border-brand-300 bg-surface',
                      )}
                    >
                      <input
                        type="radio"
                        name="report-category"
                        value={item.key}
                        checked={active}
                        onChange={() => setCategory(item.key)}
                        className="accent-brand-600 mt-0.5"
                      />
                      <span className="min-w-0">
                        <span className="text-ink block text-sm font-medium">{item.label}</span>
                        <span className="text-ink-muted block text-xs">{item.hint}</span>
                      </span>
                    </label>
                  );
                })}
              </div>
            </fieldset>

            <Textarea
              label="Details"
              required
              rows={4}
              maxLength={MAX_DETAILS}
              placeholder="Describe what happened. Include dates, messages or amounts if you can."
              hint={
                <span
                  className={cn(
                    trimmed.length > 0 && trimmed.length < MIN_DETAILS && 'text-danger-600',
                  )}
                >
                  {trimmed.length} / {MAX_DETAILS} characters · at least {MIN_DETAILS}
                </span>
              }
              value={details}
              onChange={(event) => setDetails(event.target.value)}
            />

            {report.isError && (
              <p className="text-caption text-danger-600" role="alert">
                {errorMessage(report.error, 'Could not send the report. Please try again.')}
              </p>
            )}

            <p className="bg-surface-muted/60 rounded-input text-ink-muted flex items-start gap-2 px-3 py-2.5 text-xs">
              <Icon name="shieldCheck" size={15} className="text-brand-600 mt-px shrink-0" />
              Reports are confidential and reviewed by our moderation team. The {noun} is not told
              who reported it, and nothing is removed until we have looked into it.
            </p>
          </div>
        )}
      </Modal>
    </>
  );
}
